<template>
  <div class="data-table">
    <b-table
      :fields="tableFields"
      :items="items"
      :selectable="selectable"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :busy="loading"
      :small="small"
      :borderless="borderless"
      sort-icon-left
      no-local-sorting
      no-select-on-click
      show-empty
      responsive
      striped
      hover
      empty-text="找不到資料"
      ref="dataTable"
      @row-clicked="handleClick"
      @row-selected="handleSelect"
      :thead-class="theadClass"
    >
      <template v-slot:head(selected)>
        <span class="text-info">
          <b-form-checkbox v-model="isSelectAllChecked"> </b-form-checkbox>
        </span>
      </template>
      <template v-slot:empty="scope">
        <span>{{ scope.emptyText }}</span>
      </template>
      <template v-slot:cell(selected)="{ index, rowSelected }">
        <b-form-checkbox
          :checked="rowSelected"
          @click.native.prevent="toggleItem(index)"
        ></b-form-checkbox>
      </template>
      <template v-slot:head(_mobile)>
        <div class="right-corner">
          <img
            v-if="mobileSearchable"
            v-b-modal.mobile-filter-toolbar
            src="../assets/img/icon/search.svg"
            alt="search-icon"
          />
          <img
            v-if="mobileExportable"
            class="ml-2"
            src="../assets/img/icon/export.svg"
            alt="export icon"
            @click.stop="$emit('export-click')"
            style="cursor: pointer;"
          />
        </div>
      </template>
      <template v-slot:cell(_mobile)="row" v-if="showRowDetails">
        <div class="d-flex justify-content-center">
          <img
            src="../assets/img/icon/arrow-down.svg"
            :style="{ transform: row.detailsShowing ? 'rotate(-180deg)' : '' }"
            alt="arrow down icon"
            @click.stop="mobileShowArrowDetails(row)"
            style="cursor: pointer;"
          />
        </div>
      </template>
      <template v-slot:row-details="{ item }" v-if="showRowDetails">

        <slot name="rowDetails">
          <div
            class="row mb-1 app--hidden app--mobile-shown--flex"
            v-for="field in filteredFieldsForMobileDetails"
            :key="field.key + field.label"
          >
            <template v-if="!field.hideRowInMobile">
              <div class="col-4 text-sm-right text-secondary" >
                {{ field.label }}
              </div>
              <div class="col" :style="field.rowDetailsStyle">
                <!-- handle customized row for mobile styling purpose
                  eg. in the parent <template v-slot:<ROW_KEY>="{ [item] [value] }"></template> -->
                <template v-if="$scopedSlots[field.key]">
                  <template v-if="field.formatter">
                    <slot
                      :name="field.key"
                      :item="{
                        ...item,
                        [field.key]: field.formatter(item[field.key], field.key, item)
                      }"
                      :value="field.formatter(item[field.key], field.key, item)"
                    ></slot>
                  </template>
                  <template v-else>
                    <slot :name="field.key" :item="item"></slot>
                  </template>
                </template>
                <template v-else>
                  <template v-if="field.formatter">
                    {{ field.formatter(item[field.key], field.key, item) }}
                  </template>
                  <template v-else>
                    {{ item[field.key] }}
                  </template>
                </template>
              </div>
            </template>
          </div>
          <slot name="row-details-custom" :item="item"></slot>
        </slot>
      </template>
      <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope"></slot>
      </template>
    </b-table>
    <div class="bottom" v-if="!disableItemPerPage">
      <div class="info">
        <div class="info-text">
          <span>第 {{ from }} – {{ to }} 項，</span>
          <span>共 {{ totalResult }} 項</span>
        </div>
      </div>
      <div class="length">
        <label>
          每頁顯示
          <b-form-select v-model="itemsPerPageSelected">
            <b-form-select-option v-for="item in itemsPerPageOptions" :key="item" :value="item">
              {{ item }}
            </b-form-select-option>
          </b-form-select>
        </label>
      </div>
      <div class="pagination">
        <b-pagination v-model="page"
        v-if="totalResult > 0"
        :total-rows="totalResult" :per-page="itemsPerPageSelected">
          <template #first-text>
            <!-- desktop only -->
            <span class="app--mobile-hidden">首頁</span>
            <!-- mobile only -->
            <span class="app--hidden app--mobile-shown">
              <img src="../assets/img/icon/first-page.svg" alt="first page icon" />
            </span>
          </template>
          <template #prev-text>
            <span class="app--mobile-hidden">上頁</span>
            <span class="app--hidden app--mobile-shown">
              <img src="../assets/img/icon/left-arrow.svg" alt="first page icon" />
            </span>
          </template>
          <template #next-text>
            <span class="app--mobile-hidden">下頁</span>
            <span class="app--hidden app--mobile-shown">
              <img src="../assets/img/icon/right-arrow.svg" alt="first page icon" />
            </span>
          </template>
          <template #last-text>
            <span class="app--mobile-hidden">尾頁</span>
            <span class="app--hidden app--mobile-shown">
              <img src="../assets/img/icon/last-page.svg" alt="first page icon" />
            </span>
          </template>
        </b-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BTable, BPagination, BFormCheckbox, BFormSelect, BFormSelectOption,
} from 'bootstrap-vue';

export default {
  components: {
    BTable,
    BPagination,
    BFormCheckbox,
    'b-form-select': BFormSelect,
    'b-form-select-option': BFormSelectOption,
  },
  props: {
    fields: {
      type: Array,
      default: undefined,
    },
    /** Only appears in mobile(phone or tab).
    * If it is defined,
    * an arrow icon will be shown for toggling in the end of each row in the the table.
    * Fields will be shown in rowdetails exluding values in fields unless otherwise specified.
    {..., isShownInDetails: Boolean } */
    mobileFields: {
      type: Array,
      default: undefined,
    },
    items: Array,
    totalResult: Number,
    selectable: Boolean,
    small: Boolean,
    borderless: Boolean,
    loading: Boolean,
    theadClass: String,
    options: Object,
    itemsPerPageOptions: {
      type: Array,
      default: () => [2, 15, 25, 50],
    },
    disableItemPerPage: {
      default: false,
      type: Boolean,
    },
    mobileSearchable: Boolean,
    mobileExportable: Boolean,
    showRowDetails: Boolean,
  },
  data: () => ({
    selected: [],
    itemsPerPageSelected: 15,
    page: 1,
    sortBy: null,
    sortDesc: true,
    isSelectAllChecked: false,
    windowWidth: window.innerWidth,
  }),
  computed: {
    totalPages() {
      return Math.ceil(this.totalResult / this.itemsPerPageSelected);
    },
    order() {
      return this.sortDesc ? 'desc' : 'asc';
    },
    from() {
      return (this.page - 1) * this.itemsPerPageSelected + 1 || 1;
    },
    to() {
      return this.itemsPerPageSelected * this.page;
    },
    filteredFieldsForMobileDetails() {
      const existingKeys = this.mobileFields
        // show it in details even it is duplicate
        .filter((field) => !field.isShownInDetails)
        .map((field) => field.key);
      // exclude duplicate values if mobileField exists
      if (this.mobileFields?.length) {
        return this.fields.filter((field) => !existingKeys.includes(field.key));
      }
      return [];
    },
    isMobile() {
      const mobileBreakPoint = 1096;
      return this.windowWidth <= mobileBreakPoint;
    },
    tableFields() {
      if (this.fields) {
        let selectableFields = [];
        if (this.selectable) {
          selectableFields = [{ key: 'selected', tdClass: 'selectable', thClass: 'selectable' }];
        }
        if (this.mobileFields?.length) {
          if (this.isMobile) {
            if (this.showRowDetails) {
              return [...selectableFields, ...this.mobileFields, { key: '_mobile' }];
            }
            return [...selectableFields, ...this.mobileFields];
          }
        }
        return [...selectableFields, ...this.fields];
      }
      return this.fields;
    },
  },
  watch: {
    page() {
      this.emitOptions();
    },
    itemsPerPageSelected() {
      this.emitOptions();
    },
    sortBy() {
      this.emitOptions();
    },
    order() {
      this.emitOptions();
    },
    isSelectAllChecked(checked) {
      if (checked) {
        this.$refs.dataTable.selectAllRows();
      } else {
        this.$refs.dataTable.clearSelected();
      }
    },
    items() {
      this.clearSelected();
    },
    options: {
      handler() {
        this.itemsPerPageSelected = this.options.size;
        this.page = this.options.page;
      },
      deep: true,
    },
  },
  methods: {
    handleSelect(items) {
      this.selected = items;
      this.$emit('row-selected', items);
    },
    handleClick(item) {
      this.$emit('row-clicked', item);
    },
    toggleItem(index) {
      if (this.$refs.dataTable.isRowSelected(index)) {
        this.$refs.dataTable.unselectRow(index);
      } else {
        this.$refs.dataTable.selectRow(index);
      }
    },
    emitOptions() {
      this.$emit('update:options', {
        page: this.page,
        size: this.itemsPerPageSelected,
        sortBy: this.sortBy || this.options?.sortBy || undefined,
        order: this.order,
      });
    },
    clearSelected() {
      this.isSelectAllChecked = false;
      this.$refs.dataTable.clearSelected();
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    mobileShowArrowDetails(row) {
      row.toggleDetails();
      this.$emit('mobile-arrow-click', row);
    },
  },
  created() {
    this.emitOptions();
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/transition/fade";

.data-table {
  &::v-deep {
    thead {
      position: relative;
    }

    & .b-table-empty-row {
      text-align: center;
    }

    .b-table-sticky-header,
    .table-responsive {
      margin-bottom: 0;
    }

    & th {
      white-space: nowrap;
      position: relative;
    }

    & td {
      @include respond(tab-landscape) {
        max-width: 20rem;
      }
      @include respond(phone) {
         max-width: 8.5rem;
      }

      div {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    // .b-table-details {
    //   display: none;
    // }

    @include respond(tab-landscape) {
      .b-table-details {
        display: table-row;
      }
    }

    .right-corner {
      position: absolute;
      top: 50%;
      right: -42%;
      transform: translateY(-50%);
    }
  }

  .bottom {
    padding: 1.2rem;
    border-top: 1px solid #f0f3f5;
    color: #3a4047;
    display: flex;
    justify-content: end;
    align-items: center;

    .custom-select {
      font-size: 100%;
    }

    .length {
      margin-right: 24px !important;
      margin-left: 0 !important;

      @include respond(phone) {
        margin-right: 0 !important;
      }

      label {
        white-space: nowrap;
        margin-bottom: 0;
      }

      select {
        width: auto;
        margin: 0 6px;
      }
    }

    .info {
      &-text {
        padding: 6px 0px;
        margin-right: 24px !important;
        margin-left: 0px !important;

        @include respond(tab-landscape) {
          span {
            display: block;
          }
        }
        @include respond(phone) {
          margin-right: 0.5rem !important;
        }
      }
    }

    @include respond(tab-landscape) {
      justify-content: center;
      flex-wrap: wrap;
    }

    @include respond(phone) {
      justify-content: space-around;
    }
  }
}

::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: none !important;
  border: #adb5bd solid 1px;
}

.pagination {
  margin-left: 0 !important;
  margin-right: 0 !important;

  [aria-disabled="true"] img {
    opacity: 0.4;
  }

  @include respond(tab-landscape) {
    ::v-deep {
      li {
        .page-link {
          border: none;

          &:hover {
            background: none;
          }
        }
        &:nth-child(n + 3):nth-last-child(n + 3) {
          display: none !important;
        }
      }
    }
  }

  @include respond(phone) {
    img {
      height: 1rem;
    }
  }
}

.pagination .b-pagination {
  margin-bottom: 0;
}
</style>
